import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const KEY = "sme_cart_v1";
const Ctx = createContext(null);

function load() {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [drawer, setDrawer] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* cart is a convenience, not a record of truth */
    }
  }, [items]);

  const value = useMemo(() => {
    const add = (product, opts = {}) => {
      // Guard: price-on-request products are not checkout-eligible until the
      // owner publishes a price. Caller UI shouldn't offer the add button in
      // that state, but we also refuse here as a defensive check.
      if (product.price_inr == null) return;
      setLastAdded(product.slug);
      setDrawer(true);
      setItems((prev) => {
        if (prev.some((i) => i.slug === product.slug)) return prev;
        return [
          ...prev,
          {
            slug: product.slug,
            title: product.title,
            subtitle: product.subtitle,
            sku: product.sku,
            image: product.images?.[0]?.thumb || product.images?.[0]?.url,
            price_inr: product.price_inr,
            price_per_carat_inr: product.price_per_carat_inr,
            type: product.type,
            qty: 1,
            setting: opts.setting || null,
            setting_price_inr: opts.setting_price_inr || 0,
            ring_size: opts.ring_size || null,
            energisation: opts.energisation !== false,
            addons: Array.isArray(opts.addons) ? opts.addons : [],
          },
        ];
      });
    };
    const remove = (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug));
    const update = (slug, patch) =>
      setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, ...patch } : i)));
    const removeAddon = (slug, addonId) =>
      setItems((prev) => prev.map((i) => i.slug === slug
        ? { ...i, addons: (i.addons || []).filter((a) => a.id !== addonId) } : i));
    const clear = () => setItems([]);
    const addonSum = (i) => (i.addons || []).reduce((s, a) => s + (a.price_inr || 0), 0);
    const goodsTotal = items.reduce(
      (s, i) => s + i.price_inr * i.qty + (i.setting_price_inr || 0) + addonSum(i), 0);
    return {
      items, add, remove, update, removeAddon, clear, count: items.length, goodsTotal,
      drawer,
      openDrawer: () => setDrawer(true),
      closeDrawer: () => setDrawer(false),
      lastAdded,
      lastItem: items.find((i) => i.slug === lastAdded) || null,
      inCart: (slug) => items.some((i) => i.slug === slug),
    };
  }, [items, drawer, lastAdded]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCart = () => useContext(Ctx);

export const SETTINGS = [
  { value: "", label: "Loose only", price: 0, note: "Default. We ship the stone unset, always." },
  { value: "silver-ring", label: "Silver ring", price: 2400 },
  { value: "silver-pendant", label: "Silver pendant", price: 2200 },
  { value: "panchdhatu-ring", label: "Panchdhatu ring", price: 3400 },
];

export const RING_SIZES = {
  Indian: ["8", "10", "12", "14", "16", "18", "20", "22", "24"],
  US: ["4", "5", "6", "7", "8", "9", "10", "11", "12"],
  UK: ["H", "J", "L", "N", "P", "R", "T", "V", "X"],
  "Continental Europe": ["44", "47", "50", "52", "54", "57", "59", "62", "64"],
  "Hong Kong": ["7", "9", "11", "13", "15", "17", "19", "21", "23"],
};
